import { Link2, type LucideProps } from "lucide-react";
import { useTranslations } from "next-intl";
import { createElement } from "react";
import { Button } from "@/components/ui/button";
import { SITE_ICON_INDEX } from "@/modules/site-layout/layout/site-icon-index";
import { SOCIAL_ICON_PATHS } from "@/lib/social-links/social-icon-paths";
import type { SocialNetwork } from "@/lib/social-links/verify-social-links";

/** A button of the public pages that is a link: the same button in the coded homepage and in the Pages blocks. */
export function LandingButton({ label, href, size, variant, className }: { label: string; href: string; className?: string; size?: "sm" | "default" | "lg"; variant?: "default" | "outline" | "secondary" | "ghost" }) {
  return <Button size={size} variant={variant} className={className} nativeButton={false} render={<a href={href} />}>{label}</Button>;
}

/** The small tag on an offer that is announced but not sold yet. */
export function SoonTag() {
  const t = useTranslations("whatWeDo");
  return <span className="ms-2 inline-block rounded-md bg-brand px-1.5 py-0.5 align-middle text-[0.65rem] font-extrabold uppercase leading-none tracking-wider text-brand-foreground">{t("soon")}</span>;
}

/** An icon drawn by its name; a plain link icon for a name that is no longer in the library. */
export function SiteIcon({ name, ...props }: LucideProps & { name: string }) {
  return createElement(SITE_ICON_INDEX[name.replaceAll("-", "")] ?? Link2, props);
}

/** A social network's logo, in the colour of the surrounding text. */
export function SocialIcon({ network, className }: { network: SocialNetwork; className?: string }) {
  return <svg aria-hidden viewBox="0 0 24 24" fill="currentColor" className={className}><path d={SOCIAL_ICON_PATHS[network]} /></svg>;
}

// Classes for bars that are always the brand purple, in light and dark: the theme variables are remapped
// so every shared component (buttons, borders) and the logo render white-on-purple inside them.
export const ON_BRAND = [
  "bg-brand text-brand-foreground",
  "[--foreground:var(--brand-foreground)] [--ring:var(--brand-foreground)]",
  "[--primary:var(--brand-foreground)] [--primary-foreground:var(--brand)]",
  "[--muted:color-mix(in_oklab,var(--brand-foreground)_15%,transparent)]",
  "[--border:color-mix(in_oklab,var(--brand-foreground)_30%,transparent)] [--input:var(--border)] [--background:transparent]",
  "[--logo-ring:var(--brand-foreground)] [--logo-band:var(--brand)]",
].join(" ");

// Classes for the top navigation bars: the navbar colour set in Settings > App brand (--navbar, --navbar-foreground),
// else the brand colours. Like ON_BRAND, the theme variables are remapped so buttons, borders and the logo read on it.
export const ON_NAVBAR = [
  "[--bar:var(--navbar,var(--brand))] [--bar-text:var(--navbar-foreground,var(--brand-foreground))]",
  "bg-(--bar) text-(--bar-text)",
  "[--foreground:var(--bar-text)] [--ring:var(--bar-text)]",
  "[--primary:var(--bar-text)] [--primary-foreground:var(--bar)]",
  "[--muted:color-mix(in_oklab,var(--bar-text)_15%,transparent)]",
  "[--border:color-mix(in_oklab,var(--bar-text)_30%,transparent)] [--input:var(--border)] [--background:transparent]",
  "[--logo-ring:var(--bar-text)] [--logo-band:var(--bar)]",
].join(" ");
